import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    appointmentTypes as typesApi,
    scheduling as schedulingApi,
    type AvailabilitySlot,
} from "../lib/api";
import {
    detectTimeZone,
    formatDayHeading,
    formatTime,
    type SuggestedTimesStyle,
} from "../lib/booking";
import type { SuggestedTimesAttrs } from "./editor/SuggestedTimesNode";
import { Dialog, Button, SelectBox, toast } from "@/ui";

/** Next ~10 business days as an availability window (skip weekends loosely by
 *  just asking for a 16-day calendar window — the backend availability engine
 *  filters to the type's weekly schedule). */
function previewWindow(): { from: string; to: string } {
    const from = new Date();
    const to = new Date(from.getTime() + 16 * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
}

export default function SuggestedTimesModal({
    open,
    onOpenChange,
    onInsert,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Called with the suggested-times block attributes; the caller inserts a
     *  draggable `suggestedTimes` node carrying them into the editor body. */
    onInsert: (attrs: SuggestedTimesAttrs) => void;
}) {
    const tz = useMemo(detectTimeZone, []);
    const [typeId, setTypeId] = useState("");
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [style, setStyle] = useState<SuggestedTimesStyle>("widget");
    const [generating, setGenerating] = useState(false);

    const typesQuery = useQuery({
        queryKey: ["appointment-types", "active"],
        queryFn: async () => (await typesApi.list({ active: "true" })).data,
        enabled: open,
    });

    const types = typesQuery.data ?? [];

    // Default to the first type once loaded.
    useEffect(() => {
        if (open && !typeId && types.length) setTypeId(types[0].id);
    }, [open, typeId, types]);

    // Reset selection when the modal closes or the type changes.
    useEffect(() => { setSelected(new Set()); }, [typeId]);
    useEffect(() => { if (!open) { setTypeId(""); setSelected(new Set()); setStyle("widget"); } }, [open]);

    const availabilityQuery = useQuery({
        queryKey: ["availability-preview", typeId, tz],
        queryFn: async () => {
            const { from, to } = previewWindow();
            const res = await schedulingApi.availabilityPreview({ appointmentTypeId: typeId, from, to, tz });
            return res.ok ? res.slots : [];
        },
        enabled: open && Boolean(typeId),
    });

    const slots = availabilityQuery.data ?? [];

    // Group slots by local day.
    const days = useMemo(() => {
        const order: string[] = [];
        const byDay = new Map<string, AvailabilitySlot[]>();
        for (const slot of slots) {
            const key = new Intl.DateTimeFormat("en-CA", {
                year: "numeric", month: "2-digit", day: "2-digit", timeZone: tz,
            }).format(new Date(slot.startIso));
            if (!byDay.has(key)) { byDay.set(key, []); order.push(key); }
            byDay.get(key)!.push(slot);
        }
        return order.slice(0, 10).map(day => ({ day, slots: byDay.get(day)! }));
    }, [slots, tz]);

    function toggleSlot(startIso: string) {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(startIso)) next.delete(startIso);
            else next.add(startIso);
            return next;
        });
    }

    async function generate() {
        if (!typeId || selected.size === 0) return;
        setGenerating(true);
        try {
            const orderedSlots = slots
                .map(s => s.startIso)
                .filter(iso => selected.has(iso));
            const res = await schedulingApi.suggestedTimes({ appointmentTypeId: typeId, slots: orderedSlots });
            if (!res.ok || !res.token || !res.bookingUrlBase) {
                toast.error(res.error ?? "Could not create suggested times.");
                return;
            }
            onInsert({
                bookingUrlBase: res.bookingUrlBase,
                token: res.token,
                slots: orderedSlots,
                timeZone: tz,
                slug: types.find(t => t.id === typeId)?.slug,
                style,
            });
            onOpenChange(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Could not create suggested times.");
        } finally {
            setGenerating(false);
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Panel className="sm:w-[560px]">
                <Dialog.Title>Insert suggested times</Dialog.Title>
                <Dialog.Description className="mt-1">
                    Pick a few open times. They'll be inserted as one-click booking buttons; the
                    first one booked locks the rest.
                </Dialog.Description>

                <div className="mt-4 flex flex-col gap-1">
                    <label className="text-sm font-medium text-fg">Appointment type</label>
                    <SelectBox
                        value={typeId}
                        onValueChange={setTypeId}
                        options={types.map(t => ({ value: t.id, label: `${t.name} (${t.durationMinutes} min)` }))}
                        placeholder={typesQuery.isLoading ? "Loading…" : "No active types"}
                    />
                </div>

                <div className="mt-4 max-h-72 overflow-auto rounded-md border border-border p-2">
                    {availabilityQuery.isLoading ? (
                        <p className="p-2 text-sm text-muted">Loading availability…</p>
                    ) : days.length === 0 ? (
                        <p className="p-2 text-sm text-muted">
                            {typeId ? "No open times in the next two weeks." : "Select an appointment type."}
                        </p>
                    ) : (
                        days.map(({ day, slots: daySlots }) => (
                            <div key={day} className="mb-3">
                                <div className="mb-1 text-xs font-semibold text-muted">
                                    {formatDayHeading(`${day}T12:00:00`, tz)}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {daySlots.map(slot => {
                                        const on = selected.has(slot.startIso);
                                        return (
                                            <button
                                                key={slot.startIso}
                                                type="button"
                                                onClick={() => toggleSlot(slot.startIso)}
                                                className={
                                                    "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors " +
                                                    (on
                                                        ? "border-accent bg-accent text-accent-fg"
                                                        : "border-border text-fg hover:bg-bg")
                                                }
                                            >
                                                {formatTime(slot.startIso, tz)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <p className="mt-2 text-xs text-muted">
                    {selected.size} selected · times shown in {tz}
                </p>

                <div className="mt-4 flex items-center gap-2">
                    <span className="text-sm font-medium text-fg">Style</span>
                    <div className="inline-flex rounded-md border border-border p-0.5" role="radiogroup" aria-label="Suggested times style">
                        {([
                            { value: "widget", label: "Widget" },
                            { value: "buttons", label: "Buttons" },
                            { value: "links", label: "Links" },
                            { value: "text", label: "Text" },
                        ] as const).map(opt => {
                            const on = style === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={on}
                                    onClick={() => setStyle(opt.value)}
                                    className={
                                        "rounded px-3 py-1 text-xs font-medium transition-colors " +
                                        (on
                                            ? "bg-accent text-accent-fg"
                                            : "text-muted hover:text-fg")
                                    }
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close render={<Button>Cancel</Button>} />
                    <Button
                        variant="primary"
                        disabled={selected.size === 0 || generating}
                        onClick={generate}
                    >
                        {generating ? "Inserting…" : `Insert ${selected.size || ""} time${selected.size === 1 ? "" : "s"}`}
                    </Button>
                </div>
            </Dialog.Panel>
        </Dialog.Root>
    );
}
